import React from "react";
import { MachineEventDto, MachineEventType } from "../models/edgeMonitorModels";

const FONT_SORA = "'Sora', system-ui, -apple-system, sans-serif";
const FONT_MONO = "'JetBrains Mono', 'SF Mono', Menlo, Consolas, monospace";

const COLORS = {
  border: "#E5E2D9",
  primaryText: "#1A1A1A",
  dimText: "#6B6B6B",
  rowEvenBg: "#FAFAF7",
  rowOddBg: "#FFFFFF",
  green: "#2D8C4E",
  blueGreyDot: "#3A6FA8",
  terracotta: "#D4572B",
  amber: "#D4B040",
  greyDot: "#8C9099",
  chipBg: "#F0EFE9",
  chipStatusBg: "#E5E2D9",
  chipAlarmBg: "#FCEBE3",
  chipAlarmText: "#D4572B",
  chipAgentBg: "#E8EFE6",
  chipAgentText: "#2D8C4E",
  chipProgBg: "#E8EFE6",
  chipProgText: "#2D8C4E",
};

const eventTypeLabel = (t: MachineEventType): { label: string; bg: string; text: string } => {
  switch (t) {
    case MachineEventType.StatusChanged:
      return { label: "STATUS", bg: COLORS.chipStatusBg, text: COLORS.primaryText };
    case MachineEventType.AlarmRaised:
      return { label: "ALARM", bg: COLORS.chipAlarmBg, text: COLORS.chipAlarmText };
    case MachineEventType.AlarmCleared:
      return { label: "ALARM.CLEAR", bg: COLORS.chipAgentBg, text: COLORS.chipAgentText };
    case MachineEventType.ProgramStarted:
      return { label: "PROG.START", bg: COLORS.chipProgBg, text: COLORS.chipProgText };
    case MachineEventType.ProgramEnded:
      return { label: "PROG.END", bg: COLORS.chipStatusBg, text: COLORS.primaryText };
    case MachineEventType.PartCompleted:
      return { label: "PART", bg: COLORS.chipStatusBg, text: COLORS.primaryText };
    case MachineEventType.AgentConnected:
      return { label: "AGENT.ON", bg: COLORS.chipAgentBg, text: COLORS.chipAgentText };
    case MachineEventType.AgentDisconnected:
      return { label: "AGENT.OFF", bg: COLORS.chipAlarmBg, text: COLORS.chipAlarmText };
    default:
      return { label: "EVENT", bg: COLORS.chipStatusBg, text: COLORS.primaryText };
  }
};

const formatTime = (iso: string): string => {
  try {
    const d = new Date(iso);
    return d.toISOString().slice(11, 19);
  } catch {
    return "—";
  }
};

const summarize = (e: MachineEventDto): { name: string; transition: string } => {
  // For StatusChange: name = signal key from details if present, else status enum.
  switch (e.eventType) {
    case MachineEventType.StatusChanged:
      return {
        name: e.details ?? "status",
        transition:
          e.previousStatus != null && e.newStatus != null
            ? `${e.previousStatus} → ${e.newStatus}`
            : e.newStatus != null
            ? `→ ${e.newStatus}`
            : "—",
      };
    case MachineEventType.AlarmRaised:
    case MachineEventType.AlarmCleared:
      return {
        name: e.alarmCode ?? "alarm",
        transition: e.alarmMessage ?? "",
      };
    case MachineEventType.ProgramStarted:
    case MachineEventType.ProgramEnded:
      return {
        name: e.programName ?? "program",
        transition: e.details ?? "",
      };
    case MachineEventType.AgentConnected:
    case MachineEventType.AgentDisconnected:
      return {
        name: "agent",
        transition: e.details ?? (e.eventType === MachineEventType.AgentConnected ? "online" : "offline"),
      };
    default:
      return { name: "event", transition: e.details ?? "" };
  }
};

export interface Zone3EventStreamProps {
  events: MachineEventDto[];
}

const Zone3EventStream: React.FC<Zone3EventStreamProps> = ({ events }) => {
  return (
    <div
      style={{
        width: "420px",
        flexShrink: 0,
        borderLeft: `1px solid ${COLORS.border}`,
        background: COLORS.rowOddBg,
        // align-self stretches to the row's height (which is set by Zone 2's
        // taller 7-section grid). `height: 100%` is invalid here because the
        // parent row has no fixed height — using alignSelf is the correct
        // flex pattern.
        alignSelf: "stretch",
        display: "flex",
        flexDirection: "column",
      }}
    >
      <div
        style={{
          padding: "20px 24px 12px",
          borderBottom: `1px solid ${COLORS.border}`,
        }}
      >
        <div style={{ display: "flex", justifyContent: "space-between", alignItems: "baseline" }}>
          <div
            style={{
              fontFamily: FONT_MONO,
              fontSize: "12px",
              fontWeight: 600,
              color: "#3D3D3D",
              letterSpacing: "0.08em",
              textTransform: "uppercase",
            }}
          >
            Event Stream
          </div>
          <div
            style={{
              display: "flex",
              alignItems: "center",
              gap: "6px",
              fontFamily: FONT_MONO,
              fontSize: "11px",
              color: COLORS.dimText,
            }}
          >
            <span
              style={{
                width: "6px",
                height: "6px",
                borderRadius: "50%",
                background: COLORS.green,
              }}
            />
            live · last 50
          </div>
        </div>
        <div style={{ fontFamily: FONT_MONO, fontSize: "10px", color: COLORS.dimText, marginTop: "4px" }}>
          Newest first · prepended via SignalR MachineEventAppended
        </div>
      </div>

      <div style={{ flex: 1, overflowY: "auto" }}>
        {events.length === 0 ? (
          <div
            style={{
              padding: "32px 24px",
              fontFamily: FONT_SORA,
              fontSize: "13px",
              color: COLORS.dimText,
              textAlign: "center",
            }}
          >
            No events yet
          </div>
        ) : (
          events.map((e, idx) => {
            const t = eventTypeLabel(e.eventType);
            const s = summarize(e);
            return (
              <div
                key={e.id}
                style={{
                  display: "grid",
                  gridTemplateColumns: "70px 130px 1fr auto",
                  alignItems: "center",
                  gap: "10px",
                  padding: "10px 24px",
                  background: idx % 2 === 0 ? COLORS.rowOddBg : COLORS.rowEvenBg,
                  fontFamily: FONT_MONO,
                  fontSize: "11px",
                  color: COLORS.primaryText,
                }}
              >
                <span style={{ color: COLORS.dimText }}>{formatTime(e.timestamp)}</span>
                <span style={{ fontWeight: 500, wordBreak: "break-all" }}>{s.name}</span>
                <span style={{ color: COLORS.dimText, wordBreak: "break-all" }}>{s.transition}</span>
                <span
                  style={{
                    background: t.bg,
                    color: t.text,
                    padding: "2px 8px",
                    borderRadius: "3px",
                    fontSize: "9px",
                    fontWeight: 600,
                    letterSpacing: "0.06em",
                    textTransform: "uppercase",
                    whiteSpace: "nowrap",
                  }}
                >
                  {t.label}
                </span>
              </div>
            );
          })
        )}
      </div>
    </div>
  );
};

export default Zone3EventStream;
